/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import classNames from 'classnames';
import type React from 'react';

import {
  FormFieldGroupContext,
  type FormFieldGroupContextValue,
  FormFieldMessage,
  useFormField,
} from '../_common/form-control';
import { type CommonProps } from '../_common/types';
import { Typography } from '../typography';

type FieldsetProps = {
  /** The accessible label displayed in the fieldset legend */
  label: React.ReactNode;
  /** The controls grouped by the fieldset */
  children: React.ReactNode;
  /** Assistive text displayed below the grouped controls */
  helpText?: React.ReactNode;
  /** Error message displayed below the grouped controls. When provided, it replaces `helpText` */
  errorText?: React.ReactNode;
  /** Whether the grouped field is required */
  isRequired?: boolean;
  /** Whether Required or Optional is shown at the end of the legend row */
  hasRequiredIndicator?: boolean;
  /** Whether the legend is clipped out of the layout, leaving it to screen readers only */
  hasVisuallyHiddenLabel?: boolean;
  /** Whether all native form controls in the fieldset are disabled */
  isDisabled?: boolean;
};

type FieldsetBaseProps = FieldsetProps & {
  fieldGroup?: Pick<
    FormFieldGroupContextValue,
    'name' | 'controlType' | 'value' | 'onChange'
  >;
  orientation?: 'horizontal' | 'vertical';
};

const FieldsetBase = ({
  label,
  children,
  helpText,
  errorText,
  isRequired = false,
  hasRequiredIndicator = false,
  hasVisuallyHiddenLabel = false,
  isDisabled = false,
  fieldGroup,
  orientation = 'vertical',
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'fieldset', FieldsetBaseProps>) => {
  const { field, controlProps } = useFormField({
    errorText,
    hasRequiredIndicator,
    hasVisuallyHiddenLabel,
    helpText,
    isRequired,
    label,
  });
  const classes = classNames('ndl-fieldset', className, {
    'ndl-disabled': isDisabled,
    'ndl-has-error': field.hasError,
  });
  const content = (
    <div
      className={classNames('ndl-fieldset-content', {
        'ndl-horizontal': orientation === 'horizontal',
      })}
    >
      {children}
    </div>
  );
  const fieldGroupValue =
    fieldGroup === undefined
      ? undefined
      : {
          ...fieldGroup,
          hasError: field.hasError,
          isDisabled,
          isRequired,
        };

  return (
    <fieldset
      ref={ref}
      className={classes}
      style={style}
      disabled={isDisabled}
      aria-describedby={controlProps['aria-describedby']}
      aria-invalid={controlProps['aria-invalid']}
      {...restProps}
      {...htmlAttributes}
    >
      <legend
        className={classNames('ndl-fieldset-legend', {
          'ndl-visually-hidden': hasVisuallyHiddenLabel,
        })}
      >
        <span className="ndl-form-label-row">
          <Typography
            as="span"
            variant="body-medium"
            className="ndl-form-label"
          >
            {label}
          </Typography>
          {hasRequiredIndicator && (
            <Typography
              as="span"
              variant="body-medium"
              className="ndl-form-label-indicator"
            >
              {isRequired ? 'Required' : 'Optional'}
            </Typography>
          )}
        </span>
      </legend>
      {fieldGroupValue === undefined ? (
        content
      ) : (
        <FormFieldGroupContext.Provider value={fieldGroupValue}>
          {content}
        </FormFieldGroupContext.Provider>
      )}
      <FormFieldMessage field={field} size="medium" />
    </fieldset>
  );
};

/**
 * Groups related form controls under a native fieldset and legend.
 *
 * @remarks
 * Use `RadioGroup` or `CheckboxGroup` when the children are exclusively
 * radios or checkboxes and should inherit shared group attributes.
 */
const Fieldset = (props: CommonProps<'fieldset', FieldsetProps>) => (
  <FieldsetBase {...props} />
);

Fieldset.displayName = 'Fieldset';

export { Fieldset, FieldsetBase, type FieldsetProps };
